import { useState, useMemo } from 'react'
import { FormInput } from './Fieldset'
import Pagination from './Pagination'
import { useTranslation } from 'react-i18next'

const TableView = ({
    columns = [],
    data = [],
    itemsPerPage = 10,
    className = '',
    searchKeys = ['title'],
    searchPlaceholder = 'Search...',
    search = true,
    onRowClick,
    renderRow,
}) => {
    const [currentPage, setCurrentPage] = useState(1)
    const [searchQuery, setSearchQuery] = useState('')
    const [sortConfig, setSortConfig] = useState({ key: null, direction: null })
    const { t } = useTranslation()

    // Sorting logic
    const sortedData = useMemo(() => {
        let sortableData = [...data]

        if (sortConfig.key) {
            sortableData.sort((a, b) => {
                const aVal = a[sortConfig.key]
                const bVal = b[sortConfig.key]

                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1
                return 0
            })
        }

        return sortableData
    }, [data, sortConfig])

    // Filtering
    const filteredData = useMemo(() => {
        if (!searchQuery) return sortedData
        return sortedData.filter(row =>
            searchKeys.some(key =>
                String(row[key] || '')
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase())
            )
        )
    }, [sortedData, searchQuery, searchKeys])

    const totalPages = Math.ceil(filteredData.length / itemsPerPage)
    const startIndex = (currentPage - 1) * itemsPerPage
    const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage)

    const handlePageChange = page => {
        if (page >= 1 && page <= totalPages) setCurrentPage(page)
    }

    const toggleSort = key => {
        setSortConfig(prev => {
            if (prev.key === key) {
                return {
                    key,
                    direction: prev.direction === 'asc' ? 'desc' : 'asc',
                }
            }
            return { key, direction: 'asc' }
        })
    }

    return (
        <div className={`space-y-4 ${className}`}>
            {/* Search */}
            {search && (
                <div className='flex justify-end'>
                    <div className='w-full sm:w-64'>
                        <FormInput
                            placeholder={searchPlaceholder}
                            value={searchQuery}
                            onChange={e => {
                                setSearchQuery(e.target.value)
                                setCurrentPage(1)
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Table */}
            <div className='overflow-x-auto border rounded-lg'>
                <table className='min-w-full text-sm'>
                    <thead className='text-gray-700 bg-gray-100'>
                        <tr>
                            {columns.map(col => (
                                <th key={col.key} className='px-4 py-2 font-semibold text-left'>
                                    <div className='flex items-center gap-1'>
                                        {col.label}
                                        {col.sortable && (
                                            <button
                                                onClick={() => toggleSort(col.key)}
                                                className='text-xs text-gray-500 hover:text-gray-700'
                                            >
                                                {sortConfig.key === col.key
                                                    ? sortConfig.direction === 'asc'
                                                        ? '▲'
                                                        : '▼'
                                                    : '⇅'}
                                            </button>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.length > 0 ? (
                            paginatedData.map((row, rowIndex) =>
                                renderRow ? (
                                    renderRow(row, rowIndex)
                                ) : (
                                    <tr
                                        key={row.id || rowIndex}
                                        className='border-t cursor-pointer hover:bg-gray-100'
                                        onClick={() => onRowClick?.(row)}
                                    >
                                        {columns.map(col => {
                                            return (
                                                <td key={col.key} className='px-4 py-2'>
                                                    {typeof col.render === 'function'
                                                        ? col.render(row[col.key], row)
                                                        : col.key === 'gift_aid'
                                                          ? row[col.key] === '0'
                                                              ? t('donations_table.statuses.no')
                                                              : row[col.key] === '1'
                                                                ? t('donations_table.statuses.yes')
                                                                : row[col.key]
                                                          : row[col.key]}
                                                </td>
                                            )
                                        })}
                                    </tr>
                                )
                            )
                        ) : (
                            <tr>
                                <td
                                    colSpan={columns.length}
                                    className='py-4 text-center text-gray-500'
                                >
                                    No data found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
            />
        </div>
    )
}

export default TableView
